/**
 * SuggestedUsers — Horizontal carousel of suggested users to follow.
 * Fetches from GET /api/social/discover.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { UserPlus, UserCheck, ChevronLeft, ChevronRight, Loader2, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuggestedUsers = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const fetchSuggestions = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const [sugRes, idsRes] = await Promise.all([
        fetch(`${API_URL}/api/social/discover`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/following-ids`, { credentials: 'include' }),
      ]);
      if (sugRes.ok) {
        const d = await sugRes.json();
        setSuggestions(d.suggestions || []);
      }
      if (idsRes.ok) {
        const d = await idsRes.json();
        setFollowingIds(new Set(d.following_ids || []));
      }
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const handleFollow = async (targetId) => {
    setActionLoading(targetId);
    const isFollowing = followingIds.has(targetId);
    // Optimistic update
    setFollowingIds(prev => {
      const next = new Set(prev);
      isFollowing ? next.delete(targetId) : next.add(targetId);
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/social/${isFollowing ? 'unfollow' : 'follow'}/${targetId}`, {
        method: isFollowing ? 'DELETE' : 'POST', credentials: 'include',
      });
      if (!res.ok) {
        // Revert on server error
        setFollowingIds(prev => {
          const next = new Set(prev);
          isFollowing ? next.add(targetId) : next.delete(targetId);
          return next;
        });
      }
    } catch {
      // Offline — queue the action
      try {
        const { enqueue } = await import('../lib/syncQueue');
        await enqueue({
          path: `/api/social/${isFollowing ? 'unfollow' : 'follow'}/${targetId}`,
          method: isFollowing ? 'DELETE' : 'POST',
          label: isFollowing ? 'Unfollow' : 'Follow',
        });
      } catch {}
    } finally { setActionLoading(null); }
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -220 : 220, behavior: 'smooth' });
    }
  };

  if (!user || dismissed || loading) return null;
  if (suggestions.length === 0) return null;

  return (
    <div className="mb-5 bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="suggested-users-section">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F4C430] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Suggested Foodies</h3>
            <p className="text-[11px] text-gray-500">People you might want to follow</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => scroll('left')} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" data-testid="scroll-left-btn">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => scroll('right')} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" data-testid="scroll-right-btn">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setDismissed(true)} className="ml-1 text-[11px] text-gray-400 hover:text-gray-600 font-medium" data-testid="dismiss-suggestions">
            Hide
          </button>
        </div>
      </div>

      {/* Scroll Container */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-4 scrollbar-none scroll-smooth">
        {suggestions.map((u) => {
          const uid = u.user_id;
          const amFollowing = followingIds.has(uid);
          return (
            <div key={uid} className="flex-shrink-0 w-[160px] bg-gray-50 rounded-2xl p-3.5 border border-gray-100 hover:border-[#FF6B35]/30 hover:shadow-sm transition-all" data-testid={`suggested-user-${uid}`}>
              <button onClick={() => navigate(`/profile/${uid}`)} className="w-full flex flex-col items-center text-center">
                {u.picture ? (
                  <img src={u.picture} alt={u.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm mb-2" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F4C430] flex items-center justify-center text-white font-bold text-lg ring-2 ring-white shadow-sm mb-2">
                    {(u.name || '?')[0].toUpperCase()}
                  </div>
                )}
                <p className="text-xs font-semibold text-gray-900 truncate w-full">{u.name || 'Foodie'}</p>
                {u.username && <p className="text-[10px] text-gray-500 truncate w-full">@{u.username}</p>}
                {u.cuisine_pref && <p className="text-[10px] text-[#FF6B35] mt-0.5 truncate w-full">{u.cuisine_pref}</p>}
              </button>
              <button
                onClick={() => handleFollow(uid)}
                disabled={actionLoading === uid}
                className={`w-full mt-2.5 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                  amFollowing
                    ? 'bg-white text-gray-600 border border-gray-200 hover:border-red-200 hover:text-red-500'
                    : 'bg-[#FF6B35] text-white hover:bg-[#E5592E] shadow-sm'
                }`}
                data-testid={`follow-action-${uid}`}
              >
                {actionLoading === uid ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : amFollowing ? (
                  <><UserCheck className="w-3.5 h-3.5" /> Following</>
                ) : (
                  <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuggestedUsers;
